import { useMutation } from "urql"

import { pluralize } from "../../../../../../utils/stringUtils"

import type { InstructionMutationBatchAddProductInstructionsArgs, Mutation } from "@/graphql/codegen/graphql"
import { AddInstructionsToProductDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseAddInstructionReturn {
  addInstructions: (props: { productId: number; instructionIds: string[] }) => Promise<boolean>
}

const useAddInstructionToProduct = (): UseAddInstructionReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [, add] = useMutation<Pick<Mutation, "instruction">, InstructionMutationBatchAddProductInstructionsArgs>(
    AddInstructionsToProductDocument
  )

  const addInstructions = async ({ productId, instructionIds }: { productId: number; instructionIds: string[] }) => {
    const { error, data } = await add({
      input: { productId, instructionIds },
    })

    const name = pluralize(instructionIds.length, "instruction", "instructions")
    const hasError =
      error?.message ||
      (data?.instruction.batchAddProductInstructions.__typename === "InstructionFailure" &&
        Boolean(data?.instruction.batchAddProductInstructions.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? `Failed to add ${name} to product`,
      })
      return false
    }
    notify({
      type: NotificationType.Success,
      silent: false,
      message: `${instructionIds.length} ${name} have been successfully added`,
    })
    return true
  }

  return {
    addInstructions,
  }
}

export default useAddInstructionToProduct
